import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Box,
  useMediaQuery,
  useTheme as useMuiTheme,
  Menu,
  MenuItem,
  Avatar,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  Menu as MenuIcon,
  Brightness4 as DarkModeIcon,
  Brightness7 as LightModeIcon,
  Settings as SettingsIcon,
  AccountCircle,
  Logout as LogoutIcon,
  Person as PersonIcon,
  Dashboard as DashboardIcon,
  Business as BusinessIcon,
  BarChart as BarChartIcon,
  Analytics as AnalyticsIcon,
  AdminPanelSettings as AdminPanelSettingsIcon
} from '@mui/icons-material';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import rankoLogo from '../assets/images/ranko-logo.svg';

const TopBar = () => {
  const { isAuthenticated, logout, user, hasPermission, isClient } = useAuth();
  const { darkMode, toggleDarkMode } = useTheme();
  const muiTheme = useMuiTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));
  const navigate = useNavigate();
  
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    handleClose();
    navigate('/');
  };

  const handleProfile = () => {
    navigate('/profile');
    handleClose();
  };

  const handleAdmin = () => {
    navigate('/admin');
    handleClose();
  };

  return (
    <AppBar position="static" elevation={1}>
      <Toolbar>
        <Box 
          component={Link} 
          to="/"
          sx={{
            display: 'flex', 
            alignItems: 'center', 
            textDecoration: 'none', 
            color: 'inherit',
            flexGrow: 1,
          }}
        >
          <img 
            src={rankoLogo} 
            alt="Ranko Logo" 
            style={{
              height: isMobile ? "32px" : "40px",
              width: "auto",
              marginRight: "8px"
            }}
          />
          <Typography
            variant={isMobile ? "body1" : "h6"}
            sx={{
              fontWeight: 'bold'
            }}
          >
            Ranko Tracker
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton
            color="inherit"
            onClick={toggleDarkMode}
            size="large"
            aria-label="toggle dark mode"
          >
            {darkMode ? <LightModeIcon /> : <DarkModeIcon />}
          </IconButton>

          {/* Analytics icon - only visible for non-client authenticated users */}
          {isAuthenticated() && !isClient() && (
            <IconButton
              component={Link}
              to="/analytics"
              color="inherit"
              title="Analytics"
              sx={{ mr: 1.5 }}
            >
              <AnalyticsIcon />
            </IconButton>
          )}

          {isAuthenticated() ? (
            <>
              {user && user.isAdmin && (
                <Box>
                  <MenuItem component={Link} to="/analytics" onClick={handleClose}>
                    <ListItemIcon>
                      <AnalyticsIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Analytics</ListItemText>
                  </MenuItem>
                  <MenuItem component={Link} to="/client-management" onClick={handleClose}>
                    <ListItemIcon>
                      <BusinessIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Client Management</ListItemText>
                  </MenuItem>
                  <MenuItem component={Link} to="/admin" onClick={handleClose}>
                    <ListItemIcon>
                      <AdminPanelSettingsIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Admin Dashboard</ListItemText>
                  </MenuItem>
                </Box>
              )}

              {isClient() && (
                <IconButton
                  component={Link}
                  to="/customer-dashboard"
                  color="inherit"
                  title="Plantation Dashboard"
                  sx={{ mr: 1.5 }}
                >
                  <BusinessIcon />
                </IconButton>
              )}
              
              <IconButton
                onClick={handleMenu}
                size="large"
                edge="end"
                color="inherit"
                aria-label="account of current user"
                aria-controls="menu-appbar"
                aria-haspopup="true"
              >
                <Avatar 
                  sx={{ 
                    width: 32, 
                    height: 32,
                    bgcolor: darkMode ? 'primary.dark' : 'primary.light',
                    color: 'white',
                    fontSize: '0.9rem'
                  }}
                >
                  {user?.username ? user.username.charAt(0).toUpperCase() : 'U'}
                </Avatar>
              </IconButton>
              <Menu
                id="menu-appbar"
                anchorEl={anchorEl}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                keepMounted
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                open={open}
                onClose={handleClose}
              >
                <MenuItem component={Link} to="/profile" onClick={handleClose}>
                  <ListItemIcon>
                    <PersonIcon fontSize="small" />
                  </ListItemIcon>
                  Profile
                </MenuItem>
                
                {isClient() && (
                  <MenuItem component={Link} to="/customer-dashboard" onClick={handleClose}>
                    <ListItemIcon>
                      <BusinessIcon fontSize="small" />
                    </ListItemIcon>
                    My Plantation
                  </MenuItem>
                )}
                
                <MenuItem component={Link} to="/settings" onClick={handleClose}>
                  <ListItemIcon>
                    <SettingsIcon fontSize="small" />
                  </ListItemIcon>
                  Settings
                </MenuItem>
                
                <MenuItem onClick={handleLogout}>
                  <ListItemIcon>
                    <LogoutIcon fontSize="small" />
                  </ListItemIcon>
                  Logout
                </MenuItem>
              </Menu>
            </>
          ) : (
            <IconButton
              component={Link}
              to="/login"
              color="inherit"
              size="large"
              edge="end"
              aria-label="login"
            >
              <AccountCircle />
            </IconButton>
          )}
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default TopBar; 